import { Globals } from '../globals'

/** @internal */
export enum TrackedEventAction {
    // sdk
    ERROR = 'error',
    SDK_IMPRESSION = 'sdk_impression',
    // Debug-only diagnostic paired with SDK_IMPRESSION to measure early exits by non-subscribers.
    // The not-subscribed + debug gate is applied at the HeartbeatManager call site.
    SDK_IMPRESSION_END = 'sdk_impression_end',
    DEBUG = 'debug',

    // profile
    PROFILE = 'profile',
    PROFILE_APPEND = 'profile_append',
    PROFILE_REMOVE = 'profile_remove',
    EXTERNAL_ID = 'external_id',
    DEREGISTER_EXTERNAL_ID = 'deregister_external_id',
    USER_DELETION_REQUEST = 'udr',
    OPT_OUT = 'opt_out',

    // activity tracking
    VIEW_PAGE = 'view_page',
    VIEW_SCREEN = 'view_screen',
    ANONYMOUS_VIEW_PAGE = 'anonymous_view_page',
    PAGE_TAG_VISIT = 'page_tag_visit',

    // ecomm
    ADD_TO_CART = 'add_to_cart',
    UPDATE_CART = 'update_cart',
    PURCHASE = 'purchase',
    VIEW_ITEM = 'view_item',
    SAVE_ITEM = 'save_item',
    UNSAVE_ITEM = 'unsave_item',
    COMPLETE_ITEM = 'complete_item',
    UNCOMPLETE_ITEM = 'uncomplete_item',
    RATE_ITEM = 'rate_item',
    UNRATE_ITEM = 'unrate_item',

    // prompt (permissions app message)
    PROMPT_SHOWN = 'prompt_shown',
    PROMPT_ACCEPTED = 'prompt_allow',
    PROMPT_DISMISSED = 'prompt_dismiss',
    PROMPT_NOT_SHOWN = 'prompt_not_shown',

    // permission
    PERMISSION_SHOWN = 'permission_prompted',
    PERMISSION_ACCEPTED = 'permission_accepted',
    PERMISSION_DISMISSED = 'permission_dismissed',
    PERMISSION_DENIED = 'permission_denied',
    PERMISSION_CHANGED = 'permission_changed',

    // app messages
    IAM_IMPRESSION = 'iam_impression',
    IAM_CLICK = 'iam_click',
    IAM_SUBSCRIBE = 'iam_subscribe',
    IAM_CLOSE = 'iam_close',
    IAM_NOT_SHOWN = 'iam_not_shown',

    // token / subscription
    TOKEN_RECEIVED = 'subscribed',
    TOKEN_REFRESHED = 'subscription_change',
    UNSUBSCRIBED = 'unsubscribed',
    MANUAL_UNSUBSCRIBED = 'manual_unsubscribed',
    SOFT_UNSUBSCRIBE = 'soft_unsubscribe',
    SOFT_RESUBSCRIBE = 'soft_resubscribe',

    // notification
    NOTIFICATION_IMPRESSION = 'notification_impression',
    NOTIFICATION_CLICK = 'notification_click',
    NOTIFICATION_CLOSE = 'notification_close',

    // live activity
    LIVE_ACTIVITY_REGISTER = 'la_register',
    LIVE_ACTIVITY_END = 'la_end',

    // session
    SESSION_START = 'session_start',
    SESSION_HEARTBEAT = 'session_heartbeat',
}

/**
 * Determines the endpoint responsible for handling the requested action. Possible values:
 *
 * - **[Allow Endpoint]{@link Globals._kApiAllowUrl}** - Reserved for subscription event only.
 * - **[Events Endpoint]{@link Globals._kApiEventsUrl}** - For any singular event or batches of events.
 *
 * @internal
 */
export const getActionEndpoint = (action: TrackedEventAction): string =>
    [TrackedEventAction.TOKEN_RECEIVED].includes(action) ? Globals._kApiAllowUrl : Globals._kApiEventsUrl

/**
 * Determines if the requested action requires a subscription before it is allowed
 * to be sent to its associated endpoint, determined via {@link getActionEndpoint}.
 *
 * @internal
 */
export const isSubscriptionRequiredAction = (action: TrackedEventAction): boolean =>
    [
        // external id
        TrackedEventAction.EXTERNAL_ID,
        TrackedEventAction.DEREGISTER_EXTERNAL_ID,
        // profile
        TrackedEventAction.PERMISSION_CHANGED,
        TrackedEventAction.PROFILE,
        TrackedEventAction.PROFILE_APPEND,
        TrackedEventAction.PROFILE_REMOVE,
        TrackedEventAction.PAGE_TAG_VISIT,
        // ecomm
        TrackedEventAction.PURCHASE,
        TrackedEventAction.UPDATE_CART,
        TrackedEventAction.ADD_TO_CART,
        TrackedEventAction.VIEW_ITEM,
        // notifications
        TrackedEventAction.NOTIFICATION_IMPRESSION,
        TrackedEventAction.NOTIFICATION_CLICK,
        TrackedEventAction.NOTIFICATION_CLOSE,
        TrackedEventAction.SOFT_UNSUBSCRIBE,
        TrackedEventAction.SOFT_RESUBSCRIBE,
        // subscription
        TrackedEventAction.MANUAL_UNSUBSCRIBED,
        TrackedEventAction.UNSUBSCRIBED,
        // session
        TrackedEventAction.SESSION_HEARTBEAT,
        TrackedEventAction.SESSION_START,
    ].includes(action)

/**
 * Determines if the requested action is considered disposable when the
 * current user is not subscribed.
 *
 * Every non-immediate, subscription-required action belongs here. These events
 * would otherwise accumulate in the IDB-persisted queue (which has no TTL) and
 * flush in a stale burst at subscribe time — arriving with skewed `created_at`
 * timestamps that confuse analytics and can overwrite current profile state with
 * outdated values from a prior session. The site will re-emit profile and
 * identity calls on the post-subscription page load when they actually matter.
 *
 * @internal
 */
export const isDisposablePreSubscriptionAction = (action: TrackedEventAction): boolean =>
    [
        // profile — non-state-update events that carry stale timestamps
        TrackedEventAction.PERMISSION_CHANGED,
        TrackedEventAction.PAGE_TAG_VISIT,
        // activity tracking
        TrackedEventAction.VIEW_PAGE,
        TrackedEventAction.VIEW_SCREEN,
        // ecomm
        TrackedEventAction.PURCHASE,
        TrackedEventAction.UPDATE_CART,
        TrackedEventAction.ADD_TO_CART,
        TrackedEventAction.VIEW_ITEM,
    ].includes(action)

/**
 * Determines if the requested action should skip the {@link EventManager}
 * queuing process and attempt to send immediately.
 *
 * @internal
 */
export const isImmediateAction = (action: TrackedEventAction): boolean =>
    [
        // debug
        TrackedEventAction.DEBUG,
        TrackedEventAction.SDK_IMPRESSION,
        TrackedEventAction.SDK_IMPRESSION_END,
        TrackedEventAction.PROMPT_NOT_SHOWN,
        // permission / prompt outcomes — low-volume, user-driven, attribution-critical;
        // must survive the page unload that frequently follows a prompt response (NS-2558).
        // The high-volume *_SHOWN denominators stay batched (covered by the page-lifecycle flush).
        TrackedEventAction.PERMISSION_ACCEPTED,
        TrackedEventAction.PERMISSION_DENIED,
        TrackedEventAction.PERMISSION_DISMISSED,
        TrackedEventAction.PROMPT_ACCEPTED,
        TrackedEventAction.PROMPT_DISMISSED,
        // error
        TrackedEventAction.ERROR,
        // subscription
        TrackedEventAction.TOKEN_RECEIVED,
        TrackedEventAction.MANUAL_UNSUBSCRIBED,
        TrackedEventAction.UNSUBSCRIBED,
        TrackedEventAction.USER_DELETION_REQUEST,
        // notifications
        TrackedEventAction.NOTIFICATION_IMPRESSION,
        TrackedEventAction.NOTIFICATION_CLICK,
        TrackedEventAction.NOTIFICATION_CLOSE,
        TrackedEventAction.SOFT_UNSUBSCRIBE,
        TrackedEventAction.SOFT_RESUBSCRIBE,
        // live activity
        TrackedEventAction.LIVE_ACTIVITY_REGISTER,
        TrackedEventAction.LIVE_ACTIVITY_END,
        // iam
        TrackedEventAction.IAM_CLICK,
        TrackedEventAction.IAM_SUBSCRIBE,
        TrackedEventAction.IAM_IMPRESSION,
        TrackedEventAction.IAM_CLOSE,
        // session
        TrackedEventAction.SESSION_START,
        TrackedEventAction.SESSION_HEARTBEAT,
    ].includes(action) || isDebugAction(action)

export const isDebugAction = (action: TrackedEventAction): boolean =>
    [TrackedEventAction.DEBUG, TrackedEventAction.SDK_IMPRESSION, TrackedEventAction.PROMPT_NOT_SHOWN].includes(action)

/**
 * Determines if the requested action is delivered write-ahead: stored before it is sent, sent even
 * while tracking is halted, and kept across page loads until the server accepts it or refuses it in
 * a way a retry cannot change. A 401 or 403 is kept, since an edge can return either transiently.
 *
 * Reserved for the visitor's own withdrawal and deletion requests. These halt every other event,
 * so they are the only ones that must still get through, and a send lost on unload cannot be
 * repeated by the visitor.
 *
 * @internal
 */
export const isWriteAheadAction = (action: TrackedEventAction): boolean =>
    [TrackedEventAction.OPT_OUT, TrackedEventAction.USER_DELETION_REQUEST].includes(action)
